import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  Anchor, Shield, CheckCircle, ArrowRight, ChevronRight, 
  BarChart3, FileCheck, Ship, Eye, AlertTriangle, Clock,
  Users, Globe, Zap, Lock, Star, ArrowUpRight, Menu, X,
  LayoutDashboard, FileText
} from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 }
  }
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.2], [1, 0.95]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email && company) {
      setSubmitted(true);
    }
  };

  const features = [
    {
      icon: Shield,
      title: 'RISQ 3.2 Compliant',
      description: 'Fully aligned with RightShip RISQ 3.2 guidelines. Automated questionnaire mapping ensures zero compliance gaps.',
      color: 'from-sky-500 to-blue-600'
    },
    {
      icon: Eye,
      title: 'Vessel Health Check',
      description: 'Comprehensive pre-inspection health scoring. Identify risks before they become critical findings.',
      color: 'from-emerald-500 to-teal-600'
    },
    {
      icon: FileCheck,
      title: 'Conduct Inspections',
      description: 'Dynamic inspection forms with risk-weighted scoring, photo documentation, and GPS verification.',
      color: 'from-violet-500 to-purple-600'
    },
    {
      icon: BarChart3,
      title: 'Fleet Analytics',
      description: 'AI-powered insights across your entire fleet. Benchmark performance and predict compliance trends.',
      color: 'from-amber-500 to-orange-600'
    },
    {
      icon: AlertTriangle,
      title: 'CAPA Management',
      description: 'End-to-end corrective action workflow. Track findings from identification to verification.',
      color: 'from-rose-500 to-pink-600'
    },
    {
      icon: Clock,
      title: 'Real-time Tracking',
      description: 'Monitor inspection status across vessels and fleets. Instant notifications for critical updates.',
      color: 'from-cyan-500 to-sky-600'
    },
    {
      icon: LayoutDashboard,
      title: 'Dynamic Dashboard',
      description: 'Real-time fleet overview with KPIs, compliance trends, and AI-powered readiness predictions at a glance.',
      color: 'from-indigo-500 to-blue-600'
    },
    {
      icon: FileText,
      title: 'Reports',
      description: 'Generate PDF, Excel, and Word reports with QR verification. Fleet-wide and per-vessel compliance summaries.',
      color: 'from-teal-500 to-emerald-600'
    }
  ];

  const stats = [
    { value: '500+', label: 'Vessels Inspected' },
    { value: '98.5%', label: 'Compliance Rate' },
    { value: '40%', label: 'Time Saved' },
    { value: '24/7', label: 'Support Available' }
  ];

  const testimonials = [
    {
      quote: "Vessel Navigator transformed our inspection process. We went from paper-based chaos to digital clarity in weeks.",
      author: "Maritime Operations Director",
      company: "Global Shipping Corp"
    },
    {
      quote: "The predictive analytics helped us prevent 3 potential detentions last quarter alone.",
      author: "Fleet Manager",
      company: "Pacific Lines"
    },
    {
      quote: "Finally, a platform that understands maritime compliance. The RISQ 3.2 integration is flawless.",
      author: "HSE Manager",
      company: "Nordic Maritime"
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A1628] text-white overflow-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A1628]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
                <Anchor className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight">Vessel Navigator</span>
            </div>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm text-slate-300 hover:text-white transition-colors">Features</a>
              <a href="#how-it-works" className="text-sm text-slate-300 hover:text-white transition-colors">How It Works</a>
              <button 
                onClick={() => navigate('/login')}
                className="text-sm text-slate-300 hover:text-white transition-colors font-medium"
              >
                Sign In
              </button>
              <a href="#register" className="px-5 py-2.5 bg-sky-500 hover:bg-sky-400 text-white text-sm font-semibold rounded-xl transition-all hover:shadow-lg hover:shadow-sky-500/25">
                Get Started
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-slate-300 hover:text-white"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-[#0A1628] border-t border-white/5 p-4"
          >
            <div className="flex flex-col gap-3">
              <a href="#features" className="text-sm text-slate-300 hover:text-white py-2">Features</a>
              <a href="#how-it-works" className="text-sm text-slate-300 hover:text-white py-2">How It Works</a>
              <button 
                onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}
                className="text-sm text-slate-300 hover:text-white py-2 text-left font-medium"
              >
                Sign In
              </button>
              <a href="#register" className="px-5 py-2.5 bg-sky-500 text-white text-sm font-semibold rounded-xl text-center">
                Get Started
              </a>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section */}
      <motion.section 
        style={{ opacity: heroOpacity, scale: heroScale }}
        className="relative min-h-screen flex items-center justify-center pt-20"
      >
        {/* Background Gradient Mesh */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-sky-500/20 rounded-full blur-[128px]" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-[128px]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[150px]" />
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
            backgroundSize: '64px 64px'
          }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500/10 border border-sky-500/20 rounded-full text-sky-400 text-sm font-medium mb-8"
          >
            <span className="w-2 h-2 bg-sky-400 rounded-full animate-pulse" />
            Now Accepting Registrations
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-tight mb-6"
          >
            <span className="block">Vessel Pre-Right</span>
            <span className="block bg-gradient-to-r from-sky-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Inspection Platform
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto mb-10"
          >
            AI-powered SaaS platform for RISQ 3.2 compliant vessel inspections. 
            Conduct pre-right inspections, track vessel health, and ensure maritime safety with confidence.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a 
              href="#register"
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-sky-500 hover:bg-sky-400 text-white font-semibold rounded-2xl transition-all hover:shadow-xl hover:shadow-sky-500/25 hover:-translate-y-0.5"
            >
              Register Now
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <a 
              href="#how-it-works"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-2xl border border-white/10 transition-all"
            >
              See How It Works
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto"
          >
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-sky-400 to-blue-400 bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="text-sm text-slate-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center p-2">
            <div className="w-1.5 h-2 bg-white/40 rounded-full" />
          </div>
        </motion.div>
      </motion.section>

      {/* Features Section */}
      <section id="features" className="py-24 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-slate-300 text-sm font-medium mb-6">
              <Zap className="w-4 h-4 text-sky-400" />
              Platform Features
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              Everything You Need for
              <span className="block bg-gradient-to-r from-sky-400 to-cyan-400 bg-clip-text text-transparent">
                Maritime Compliance
              </span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-slate-400 max-w-2xl mx-auto">
              From pre-inspection planning to post-inspection reporting, Vessel Navigator covers the complete lifecycle.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {features.map((feature, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className="group relative p-6 md:p-8 rounded-3xl bg-white/[0.02] border border-white/[0.05] hover:bg-white/[0.04] hover:border-white/10 transition-all duration-300"
              >
                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-slate-400 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 md:py-32 relative bg-gradient-to-b from-transparent via-sky-500/5 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-slate-300 text-sm font-medium mb-6">
              <Ship className="w-4 h-4 text-sky-400" />
              How It Works
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              Simple Steps to
              <span className="block bg-gradient-to-r from-sky-400 to-cyan-400 bg-clip-text text-transparent">
                Full Compliance
              </span>
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              {
                step: '01',
                title: 'Register & Configure',
                description: 'Sign up for early access, add your fleet details, and configure inspection parameters.',
                icon: Users
              },
              {
                step: '02',
                title: 'Conduct Inspections',
                description: 'Use RISQ 3.2 questionnaires to conduct thorough pre-right inspections onboard or remotely.',
                icon: FileCheck
              },
              {
                step: '03',
                title: 'Track & Improve',
                description: 'Monitor vessel health scores, manage corrective actions, and maintain continuous compliance.',
                icon: BarChart3
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className="relative text-center md:text-left"
              >
                <div className="text-6xl font-bold text-white/5 mb-4">{item.step}</div>
                <div className="w-14 h-14 rounded-2xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center mb-5 mx-auto md:mx-0">
                  <item.icon className="w-7 h-7 text-sky-400" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-slate-400 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-slate-300 text-sm font-medium mb-6">
              <Star className="w-4 h-4 text-amber-400" />
              Trusted by Industry Leaders
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl md:text-5xl font-bold">
              What Our Users Say
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-6"
          >
            {testimonials.map((testimonial, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className="p-8 rounded-3xl bg-white/[0.02] border border-white/[0.05]"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-slate-300 leading-relaxed mb-6">"{testimonial.quote}"</p>
                <div>
                  <div className="font-semibold">{testimonial.author}</div>
                  <div className="text-sm text-slate-500">{testimonial.company}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA / Registration Section */}
      <section id="register" className="py-24 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-sky-500/10 via-blue-600/10 to-cyan-500/10 border border-white/10"
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-30">
              <div className="absolute inset-0" style={{
                backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.1) 1px, transparent 0)`,
                backgroundSize: '32px 32px'
              }} />
            </div>

            <div className="relative z-10 p-8 md:p-16">
              <div className="max-w-3xl mx-auto text-center mb-12">
                <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500/20 border border-sky-500/30 rounded-full text-sky-300 text-sm font-medium mb-6">
                  <Lock className="w-4 h-4" />
                  Limited Spots Available
                </motion.div>
                <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
                  Ready to Transform Your
                  <span className="block bg-gradient-to-r from-sky-400 to-cyan-400 bg-clip-text text-transparent">
                    Inspection Process?
                  </span>
                </motion.h2>
                <motion.p variants={fadeInUp} className="text-lg text-slate-400">
                  Be among the first to experience next-generation vessel compliance management.
                </motion.p>
              </div>

              {!submitted ? (
                <motion.form
                  variants={fadeInUp}
                  onSubmit={handleSubmit}
                  className="max-w-xl mx-auto"
                >
                  <div className="flex flex-col sm:flex-row gap-4">
                    <input
                      type="text"
                      placeholder="Company Name"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      required
                      className="flex-1 px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-sky-500/50 focus:ring-2 focus:ring-sky-500/20 transition-all"
                    />
                    <input
                      type="email"
                      placeholder="Work Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="flex-1 px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-sky-500/50 focus:ring-2 focus:ring-sky-500/20 transition-all"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full mt-4 px-8 py-4 bg-sky-500 hover:bg-sky-400 text-white font-semibold rounded-xl transition-all hover:shadow-xl hover:shadow-sky-500/25 hover:-translate-y-0.5 flex items-center justify-center gap-2"
                  >
                    Reserve My Spot
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  <p className="text-center text-sm text-slate-500 mt-4">
                    No credit card required • Free to get started
                  </p>
                </motion.form>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="max-w-xl mx-auto text-center"
                >
                  <div className="w-16 h-16 bg-emerald-500/20 border border-emerald-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-emerald-400" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">You're on the List!</h3>
                  <p className="text-slate-400">
                    Thank you for registering. We'll reach out to <span className="text-white">{email}</span> with your invitation soon.
                  </p>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
                  <Anchor className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold">Vessel Navigator</span>
              </div>
              <p className="text-slate-400 max-w-md">
                AI-powered maritime compliance platform for vessel pre-right inspections. 
                Aligned with RISQ 3.2 guidelines for comprehensive vessel health management.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Platform</h4>
              <ul className="space-y-3 text-slate-400">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a></li>
                <li><a href="#register" className="hover:text-white transition-colors">Get Started</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-3 text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="/contact" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-8 border-t border-white/5">
            <p className="text-sm text-slate-500">
              © 2026 Vesselops360. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Globe className="w-5 h-5 text-slate-500 hover:text-white cursor-pointer transition-colors" />
              <Shield className="w-5 h-5 text-slate-500 hover:text-white cursor-pointer transition-colors" />
              <Lock className="w-5 h-5 text-slate-500 hover:text-white cursor-pointer transition-colors" />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
